<template>
  <!--First Table-->
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('attendance.on_going_attendance') }}
      </oxd-text>
      <oxd-divider />
    </div>
    <br />
    <div class="orangehrm-paper-container">
      <table-header
        :total="totalFirst"
        :loading="isLoadingFirst"
        @delete="onClickDeleteSelected"
      ></table-header>
      <div class="orangehrm-container">
        <oxd-card-table
          ref="cardTable"
          :headers="headersFirst"
          :items="itemsFirst?.data"
          :clickable="true"
          :loading="isLoadingFirst"
          class="orangehrm-employee-list"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginatorFirst"
          v-model:current="currentPageFirst"
          :length="pagesFirst"
        />
      </div>
    </div>
    <delete-confirmation ref="deleteDialog"></delete-confirmation>
  </div>

  <!--Second Table-->
  <div class="orangehrm-background-container">
    <oxd-table-filter :filter-title="$t('attendance.my_fingerspot_attendance')">
      <oxd-form @submit-valid="filterItems" @reset="filterItems">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.status"
                type="select"
                :label="$t('attendance.processed_fingerspot')"
                :options="includeOpts"
                :show-empty-selector="false"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <oxd-button
            display-type="ghost"
            :label="$t('general.reset')"
            type="reset"
          />
          <oxd-button
            class="orangehrm-left-space"
            display-type="secondary"
            :label="$t('general.search')"
            type="submit"
          />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>
    <br />
    <div class="orangehrm-paper-container">
      <table-header
        :selected="checkedItems.length"
        :total="total"
        :loading="isLoading"
        @delete="onClickDeleteSelected"
      ></table-header>
      <div class="orangehrm-container">
        <oxd-card-table
          ref="cardTable"
          v-model:selected="checkedItems"
          v-model:order="sortDefinition"
          :headers="headers"
          :items="items?.data"
          :clickable="true"
          :loading="isLoading"
          class="orangehrm-employee-list"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
    </div>
    <delete-confirmation ref="deleteDialog"></delete-confirmation>
  </div>
</template>

<script>
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import {APIService} from '@/core/util/services/api.service';
import useSort from '@ohrm/core/util/composable/useSort';
import {computed, ref} from 'vue';
import {navigate} from '@ohrm/core/util/helper/navigation';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import moment from 'moment';
import usei18n from '@/core/util/composable/usei18n';
import useLocale from '@/core/util/composable/useLocale';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {
  parseDate,
  parseTime,
  formatTime,
  formatDate,
  getStandardTimezone,
} from '@/core/util/helper/datefns';
import RecordCell from '@/orangehrmAttendancePlugin/components/RecordCell.vue';

const defaultSortOrder = {};

export default {
  components: {
    'delete-confirmation': DeleteConfirmationDialog,
  },
  setup() {
    const {$t} = usei18n();
    const {locale} = useLocale();
    const {jsDateFormat, timeFormat, jsTimeFormat} = useDateFormat();

    // First table
    const includeOptsFirst = [
      {
        id: 0,
        param: '',
        label: '-',
      },
      {
        id: 1,
        param: '',
        label: 'All',
      },
      {
        id: 2,
        param: '1',
        label: 'Pending',
      },
      {
        id: 3,
        param: '2',
        label: 'Sent',
      },
      {
        id: 4,
        param: '3',
        label: 'Revision',
      },
      {
        id: 5,
        param: '4',
        label: 'Rejected',
      },
      {
        id: 6,
        param: '5',
        label: 'Approved',
      },
      {
        id: 7,
        param: '6',
        label: 'Cancelled',
      },
    ];

    const statusMap = includeOptsFirst.reduce((acc, cur) => {
      acc[cur.param] = cur.label;
      return acc;
    }, {});

    const serializedFiltersFirst = computed(() => {
      return {
        filter: 'onGoingAttendance',
      };
    });

    const httpFirst = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/my/attendance/records',
    );

    const attendanceRecordNormalizer = (data) => {
      return data.map((item) => {
        const {punchIn, punchOut} = item;
        const punchInDate = formatDate(
          parseDate(punchIn?.userDate),
          jsDateFormat,
          {locale},
        );
        const punchInTime = formatTime(
          parseTime(punchIn?.userTime, timeFormat),
          jsTimeFormat,
        );
        const punchOutDate = formatDate(
          parseDate(punchOut?.userDate),
          jsDateFormat,
          {locale},
        );
        const punchOutTime = formatTime(
          parseTime(punchOut?.userTime, timeFormat),
          jsTimeFormat,
        );

        return {
          id: item.id,
          punchIn: {
            ...punchIn,
            userTime: punchInTime,
            userDate: punchInDate,
          },
          punchOut: {
            ...punchOut,
            userTime: punchOutTime,
            userDate: punchOutDate,
          },
          punchInNote: punchIn.note,
          punchOutNote: punchOut.note,
          duration: item.duration,
          adjustmentStatus: item.submission?.status
            ? statusMap[item.submission?.status]
            : statusMap[0],
          submission: item.submission,
        };
      });
    };

    const {
      total: totalFirst,
      pages: pagesFirst,
      pageSize: pageSizeFirst,
      response: responseFirst,
      isLoading: isLoadingFirst,
      execQuery: execQueryFirst,
      currentPage: currentPageFirst,
      showPaginator: showPaginatorFirst,
    } = usePaginate(httpFirst, {
      query: serializedFiltersFirst,
      normalizer: attendanceRecordNormalizer,
      toastNoRecords: false,
    });

    const totalDuration = computed(() => {
      const meta = response.value?.meta;
      return meta ? meta.sum.label : '0.00';
    });

    const more = {
      component: 'oxd-table-dropdown',
      props: {
        options: [],
        style: {'margin-left': 'auto'},
        onClick: null,
      },
    };

    // Second table
    const dataNormalizer = (data) => {
      return data.map((item) => {
        return {
          id: item.id,
          date: moment(item.scanDate.date, 'YYYY-MM-DD HH:mm:ss.SSSSSS').format(
            'dddd, D MMMM YYYY hh:mm:ss A',
          ),
          message: item.message,
          status: item.status,
          isSelectable: true,
          ioMode:
            item.ioMode === '1'
              ? $t('attendance.punch_in')
              : $t('attendance.punch_out'),
        };
      });
    };
    const includeOpts = [
      {
        id: 1,
        param: '',
        label: 'All',
      },
      {
        id: 2,
        param: 'POSTED',
        label: 'POSTED',
      },
      {
        id: 3,
        param: 'DRAFT',
        label: 'DRAFT',
      },
      {
        id: 4,
        param: 'ERROR',
        label: 'ERROR',
      },
    ];
    const filters = ref({
      status: includeOpts[0],
    });
    const {sortDefinition, sortField, sortOrder, onSort} = useSort({
      sortDefinition: defaultSortOrder,
    });
    const serializedFilters = computed(() => {
      return {
        status: filters.value.status?.param,
        sortField: sortField.value,
        sortOrder: sortOrder.value,
      };
    });

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/my-fingerspot-records',
    );
    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      query: serializedFilters,
      normalizer: dataNormalizer,
    });

    onSort(execQuery);

    return {
      httpFirst,
      totalFirst,
      pagesFirst,
      pageSizeFirst,
      isLoadingFirst,
      execQueryFirst,
      currentPageFirst,
      showPaginatorFirst,
      itemsFirst: responseFirst,
      totalDuration,
      statusMap,
      more,
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
      filters,
      sortDefinition,
      includeOpts,
    };
  },
  data() {
    return {
      checkedItems: [],
    };
  },
  computed: {
    headersFirst() {
      return [
        {
          name: 'punchIn',
          slot: 'title',
          title: this.$t('attendance.punch_in'),
          style: {flex: 1},
          cellRenderer: this.cellRenderer,
        },
        {
          name: 'punchInNote',
          slot: 'title',
          cellType: 'oxd-table-cell-truncate',
          title: this.$t('attendance.punch_in_note'),
          style: {flex: 1},
        },
        {
          name: 'punchOut',
          slot: 'title',
          title: this.$t('attendance.punch_out'),
          style: {flex: 1},
          cellRenderer: this.cellRenderer,
        },
        {
          name: 'punchOutNote',
          slot: 'title',
          cellType: 'oxd-table-cell-truncate',
          title: this.$t('attendance.punch_out_note'),
          style: {flex: 1},
        },
        {
          name: 'duration',
          slot: 'title',
          title: this.$t('attendance.duration_hours'),
          style: {flex: 1},
        },
        {
          name: 'adjustmentStatus',
          slot: 'title',
          title: this.$t('attendance.attendance_adjustment_status'),
          style: {flex: 1},
        },
      ];
    },
    headers() {
      return [
        {
          name: 'date',
          title: this.$t('general.time'),
          sortField: 'scanDate.date',
          style: {flex: 1},
        },
        {
          name: 'ioMode',
          title: this.$t('attendance.attendance_io_mode'),
          style: {flex: 1},
        },
        {
          name: 'message',
          title: this.$t('general.message'),
          sortField: 'message',
          style: {flex: 1},
        },
        {
          name: 'status',
          title: this.$t('attendance.processed_fingerspot'),
          sortField: 'status',
          style: {flex: 1},
        },
        {
          name: 'actions',
          slot: 'action',
          title: this.$t('general.actions'),
          style: {flex: 1},
          cellType: 'oxd-table-cell-actions',
          cellRenderer: this.actionRenderer,
        },
      ];
    },
  },
  methods: {
    actionRenderer(...[, , , row]) {
      const cellConfig = {};
      const dropdownActions = [];
      dropdownActions.push({
        label: this.$t('attendance.attendance_make_adjustment'),
        context: 'make_adjustment',
      });
      if (row.status === 'ERROR' || row.status === 'DRAFT') {
        cellConfig.edit = {
          onClick: () => this.onClickEdit(row),
          props: {
            name: 'pencil-fill',
          },
        };
        if (dropdownActions.length > 0) {
          this.more.props.options = dropdownActions;
          this.more.props.onClick = ($event) =>
            this.onDropdownAction($event, row);
          cellConfig.more = this.more;
        }
      }
      return {
        props: {
          header: {
            cellConfig,
          },
        },
      };
    },
    cellRenderer(...args) {
      const cellData = args[1];
      return {
        component: RecordCell,
        props: {
          date: cellData.userDate,
          time: cellData.userTime,
          offset: getStandardTimezone(cellData.offset),
        },
      };
    },
    onDropdownAction($event, item) {
      const attendanceMode = item.ioMode === 'Punch In' ? 'in' : 'out';
      const parsedDate = moment(item.date, 'dddd, D MMMM YYYY hh:mm:ss A');
      const dateFormatted = parsedDate.format('YYYY-MM-DD');
      const timeFormatted = parsedDate.format('HH:mm');

      const param = {
        attendanceMode,
        [`punch${
          attendanceMode.charAt(0).toUpperCase() + attendanceMode.slice(1)
        }UserDate`]: dateFormatted,
        [`punch${
          attendanceMode.charAt(0).toUpperCase() + attendanceMode.slice(1)
        }UserTime`]: timeFormatted,
      };

      const queryString = new URLSearchParams(param).toString();
      navigate(`/attendance/addAttendanceAdjustment?${queryString}`);
    },

    onClickEdit($event) {
      const id = $event.id ? $event.id : $event.item?.id;
      navigate('/attendance/editMyFingerspotRecord/{id}', {id});
    },
    onClickDeleteSelected() {
      const ids = this.checkedItems.map((index) => {
        return this.items?.data[index].id;
      });
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems(ids);
        }
      });
    },
    onClickDelete(item, $event) {
      $event.stopImmediatePropagation();
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems([item.id]);
        }
      });
    },
    deleteItems(items) {
      if (items instanceof Array) {
        this.isLoading = true;
        this.http
          .deleteAll({
            ids: items,
          })
          .then(() => {
            return this.$toast.deleteSuccess();
          })
          .then(() => {
            this.isLoading = false;
            this.resetDataTable();
          });
      }
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
    async filterItems() {
      await this.execQuery();
    },
  },
};
</script>

<style src="./fingerspot-attendance.scss" lang="scss" scoped></style>
